import api from './api';
import {
  PerformanceReview,
  PerformanceReviewTemplate,
  RawReviewContent,
  ReviewStatus,
  SpringPageResponse,
} from '../types';

/** Pagination-only filters, for endpoints that accept no other query params. */
export interface PaginationFilters {
  page?: number;
  limit?: number;
}

export interface PerformanceReviewFilters extends PaginationFilters {
  memberId?: string;
  status?: ReviewStatus;
}

export interface PaginatedPerformanceReviews {
  reviews: PerformanceReview[];
  total: number;
  page: number;
  totalPages: number;
}

export interface CreatePerformanceReviewDto {
  templateId: string;
  memberId: string;
}

export interface SaveBaseTemplateDto {
  name: string;
  templateContent: RawReviewContent;
}

/** Type guard for Spring Boot Page<T> responses. */
const isSpringPage = <T>(data: unknown): data is SpringPageResponse<T> =>
  typeof data === 'object' && data !== null && 'content' in data;

/**
 * Performance Review Service
 * Centralizes performance-review instance and template API calls. Paths are
 * relative to the axios baseURL which already ends in `/api`.
 */
class PerformanceReviewService {
  private baseUrl = '/performance-reviews';

  private templatesUrl = '/performance-review-templates';

  private buildPageParams(filters?: PerformanceReviewFilters): URLSearchParams {
    const params = new URLSearchParams();
    if (filters?.page) {
      // Spring Boot uses 0-indexed pages
      params.append('page', (filters.page - 1).toString());
    }
    if (filters?.limit) {
      params.append('size', filters.limit.toString());
    }
    if (filters?.memberId) params.append('memberId', filters.memberId);
    if (filters?.status) params.append('status', filters.status);
    return params;
  }

  private buildUrl(path: string, params: URLSearchParams): string {
    return params.toString() ? `${path}?${params.toString()}` : path;
  }

  private unwrapReviews(data: unknown, fallbackPage: number): PaginatedPerformanceReviews {
    if (isSpringPage<PerformanceReview>(data)) {
      return {
        reviews: data.content,
        total: data.totalElements,
        page: data.number + 1,
        totalPages: data.totalPages,
      };
    }
    const reviews = (data as PerformanceReview[]) ?? [];
    return { reviews, total: reviews.length, page: fallbackPage, totalPages: 1 };
  }

  /** Get the organization's base review template. */
  async getBaseTemplate(): Promise<PerformanceReviewTemplate> {
    const response = await api.get<PerformanceReviewTemplate>(`${this.templatesUrl}/base`);
    return response.data;
  }

  /** Admin: create or update the organization's base review template. */
  async saveBaseTemplate(data: SaveBaseTemplateDto): Promise<PerformanceReviewTemplate> {
    const response = await api.post<PerformanceReviewTemplate>(`${this.templatesUrl}/base`, data);
    return response.data;
  }

  /** Manager/Admin: list review instances (scoped server-side by role). */
  async list(filters?: PerformanceReviewFilters): Promise<PaginatedPerformanceReviews> {
    const response = await api.get(this.buildUrl(this.baseUrl, this.buildPageParams(filters)));
    return this.unwrapReviews(response.data, filters?.page ?? 1);
  }

  /** Manager/Admin: get a single review instance. */
  async get(id: string): Promise<PerformanceReview> {
    const response = await api.get<PerformanceReview>(`${this.baseUrl}/${id}`);
    return response.data;
  }

  /** Manager/Admin: create a DRAFT review instance for a member from a template. */
  async create(data: CreatePerformanceReviewDto): Promise<PerformanceReview> {
    const response = await api.post<PerformanceReview>(this.baseUrl, data);
    return response.data;
  }

  /** Manager/Admin: replace a review instance's content. */
  async update(id: string, reviewContent: RawReviewContent): Promise<PerformanceReview> {
    const response = await api.put<PerformanceReview>(`${this.baseUrl}/${id}`, { reviewContent });
    return response.data;
  }

  /** Manager/Admin: delete a review instance. */
  async remove(id: string): Promise<void> {
    await api.delete(`${this.baseUrl}/${id}`);
  }

  /** Manager/Admin: publish a review instance, making it visible to the member. */
  async publish(id: string): Promise<PerformanceReview> {
    const response = await api.patch<PerformanceReview>(`${this.baseUrl}/${id}/publish`);
    return response.data;
  }

  /** Member: list the current member's published reviews. */
  async listMine(filters?: PaginationFilters): Promise<PaginatedPerformanceReviews> {
    const response = await api.get(this.buildUrl(`${this.baseUrl}/me`, this.buildPageParams(filters)));
    return this.unwrapReviews(response.data, filters?.page ?? 1);
  }

  /** Member: get one of the current member's published reviews. */
  async getMine(id: string): Promise<PerformanceReview> {
    const response = await api.get<PerformanceReview>(`${this.baseUrl}/me/${id}`);
    return response.data;
  }
}

// Export singleton instance
export const performanceReviewService = new PerformanceReviewService();
